<template>
  <div class="optix-chart-card">
    <div class="chart-title">
      Score distribution by tier
    </div>

    <div class="chart-canvas-wrap">
      <canvas ref="chartCanvas" />
    </div>

    <div
      v-if="!totalProducts"
      class="chart-empty-state"
    >
      No products to chart yet.
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  name: 'ProductReportsScoreChart',

  props: {
    products: {
      type: Array,
      default: () => ([]),
    },

    scoreKey: {
      type: String,
      default: 'optix_score',
    },
  },

  data() {
    return {
      chart: null,
    };
  },

  computed: {
    scores() {
      return this.products
        .map((product) => this.normalizePercent(product?.[this.scoreKey]))
        .filter((score) => score !== null);
    },

    totalProducts() {
      return this.scores.length;
    },

    passCount() {
      return this.scores.filter((score) => score >= 70).length;
    },

    warningCount() {
      return this.scores.filter((score) => score >= 50 && score < 70).length;
    },

    failCount() {
      return this.scores.filter((score) => score < 50).length;
    },

    chartLabels() {
      return ['Pass (≥70)', 'Warning (50–69)', 'Fail (<50)'];
    },

    chartValues() {
      return [
        this.passCount,
        this.warningCount,
        this.failCount,
      ];
    },

    chartMax() {
      const maxValue = Math.max(...this.chartValues, 1);
      const rounded = Math.ceil(maxValue / 2) * 2;

      return Math.max(rounded, 2);
    },
  },

  watch: {
    chartValues() {
      this.renderChart();
    },
  },

  mounted() {
    this.renderChart();
  },

  beforeDestroy() {
    this.destroyChart();
  },

  methods: {
    normalizePercent(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      if (typeof value === 'number') {
        return Math.round(value);
      }

      const numeric = parseFloat(String(value).replace('%', '').trim());

      return Number.isNaN(numeric) ? null : Math.round(numeric);
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },

    renderChart() {
      this.$nextTick(() => {
        const canvas = this.$refs.chartCanvas;

        if (!canvas) {
          return;
        }

        this.destroyChart();

        this.chart = new Chart(canvas, {
          type: 'bar',
          data: {
            labels: this.chartLabels,
            datasets: [
              {
                label: 'Products',
                data: this.chartValues,
                backgroundColor: [
                  '#58c06c',
                  '#eda934',
                  '#e5524f',
                ],
                borderColor: [
                  '#58c06c',
                  '#eda934',
                  '#e5524f',
                ],
                borderWidth: 1,
                borderRadius: 4,
                borderSkipped: false,
                barPercentage: 0.8,
                categoryPercentage: 0.82,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
              duration: 250,
            },
            layout: {
              padding: {
                top: 24,
                right: 18,
                bottom: 6,
                left: 4,
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                backgroundColor: '#111827',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                titleFont: {
                  size: 13,
                  weight: '700',
                },
                bodyFont: {
                  size: 13,
                  weight: '600',
                },
                padding: 10,
                displayColors: false,
                callbacks: {
                  label: (context) => {
                    const value = context.parsed.y || 0;
                    return `${value} ${value === 1 ? 'product' : 'products'}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: {
                  display: true,
                  drawTicks: false,
                  color: '#edf2f7',
                  borderColor: '#9ca3af',
                  borderDash: [3, 3],
                },
                ticks: {
                  color: '#555f70',
                  font: {
                    size: 13,
                    weight: '600',
                  },
                },
              },
              y: {
                beginAtZero: true,
                max: this.chartMax,
                ticks: {
                  stepSize: Math.max(Math.ceil(this.chartMax / 4), 1),
                  precision: 0,
                  color: '#6b7280',
                  font: {
                    size: 12,
                    weight: '600',
                  },
                },
                grid: {
                  color: '#e5eaf2',
                  borderColor: '#9ca3af',
                  borderDash: [3, 3],
                  drawTicks: false,
                },
              },
            },
          },
          plugins: [
            {
              id: 'optixValueLabels',
              afterDatasetsDraw: (chart) => {
                const { ctx } = chart;

                ctx.save();
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';
                ctx.font = '700 13px Arial, sans-serif';

                chart.data.datasets.forEach((dataset, datasetIndex) => {
                  const meta = chart.getDatasetMeta(datasetIndex);

                  meta.data.forEach((bar, index) => {
                    const value = dataset.data[index];

                    if (!value) {
                      return;
                    }

                    ctx.fillStyle = Array.isArray(dataset.backgroundColor)
                      ? dataset.backgroundColor[index]
                      : '#111827';

                    ctx.fillText(value, bar.x, bar.y - 6);
                  });
                });

                ctx.restore();
              },
            },
          ],
        });
      });
    },
  },
};
</script>

<style scoped>
.optix-chart-card {
  position: relative;
  width: 100%;
  min-height: 360px;
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 12px;
  padding: 18px;
}

.chart-title {
  color: #64748b;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 14px;
}

.chart-canvas-wrap {
  position: relative;
  width: 100%;
  height: 285px;
}

.chart-empty-state {
  position: absolute;
  inset: 0;
  top: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.72);
}

@media (max-width: 767.98px) {
  .optix-chart-card {
    overflow-x: auto;
  }

  .chart-canvas-wrap {
    min-width: 680px;
  }
}
</style>
